/**
 * When a new screen counts as shown, for the app's `screen` message and its route timing (T-0320).
 *
 * While the session is being checked (`ProtectedRoute`) or a route's code is loading (`Layout`'s
 * "Loading page"), the address has changed and the screen has not. Both placeholders carry
 * `data-route-pending`, and the message waits until none is left and a frame has been painted, so
 * the shell's title, its `ready` and the timing all describe the screen the person sees.
 */
const ROUTE_PLACEHOLDER = '[data-route-pending]';

/** Longer than any route chunk from a warm cache; past it the message goes anyway. */
const SETTLE_LIMIT_MS = 10_000;

/** Run `callback` once the screen has settled. Returns a cancel for an address that moved on. */
export function whenScreenSettles(callback: () => void): () => void {
  const started = performance.now();
  let frame = 0;
  let cancelled = false;
  const check = () => {
    if (cancelled) return;
    const waiting =
      document.querySelector(ROUTE_PLACEHOLDER) !== null &&
      performance.now() - started < SETTLE_LIMIT_MS;
    if (waiting) {
      frame = requestAnimationFrame(check);
      return;
    }
    // One more frame, so what is measured has been painted and not only committed.
    frame = requestAnimationFrame(() => {
      if (!cancelled) callback();
    });
  };
  frame = requestAnimationFrame(check);
  return () => {
    cancelled = true;
    cancelAnimationFrame(frame);
  };
}

/**
 * Longer than any screen's own refetch under load; past it the caller gets its callback anyway.
 * Also the longest the launch wait holds over a page whose content never comes in (WAIT-9), which
 * the e2e screenshot wait outlasts (`e2e/support/launchSettled.ts`).
 */
export const DATA_SETTLE_LIMIT_MS = 10_000;

/**
 * IU-14: run `callback` once nothing in `<main>` is `aria-busy="true"` — the marker several screens
 * already set while they refetch (`WorkAreasDecisionsPage`, `AgentsPage`, …). `ShellRuntime` uses
 * this to answer the shell's `refresh` only once the pull actually refilled the screen, not on a
 * fixed delay. A screen that sets no such marker settles on the first check, the same as a screen
 * with nothing left to load.
 */
export function whenDataSettles(callback: () => void): () => void {
  return whenNoneBusy('main [aria-busy="true"]', callback);
}

/**
 * I-1167: run `callback` once the screen's *content* has filled in — nothing in `<main>` but a
 * control is `aria-busy`. A control is busy while its permission is still being read
 * (`gateControlProps`'s pending state, drawn undimmed), which changes nothing the person sees and
 * can last as long as the screen, so it never holds this back.
 */
export function whenContentSettles(callback: () => void): () => void {
  return whenNoneBusy(
    'main [aria-busy="true"]:not(button, a, input, select, textarea, [role="button"])',
    callback,
  );
}

/**
 * I-1234 (F-0534, `docs/concept-per-person-data.md`, LS-2): what still marks a screen as loading —
 * a route placeholder, a pulsing placeholder (every skeleton, the scope line's, a row's), or content
 * marked `aria-busy`. A control's busy state is not loading: it can last as long as the screen.
 * I-2056 (reopened 2026-11-01): a redirect still waiting on the work-area list
 * (`data-redirect-pending`, the home's "Loading work areas") is not the screen either: the launch
 * faded onto it, and the page it went on to replaced it 1.2 to 1.6 s later.
 */
export const CONTENT_PENDING =
  '[data-route-pending], [data-redirect-pending], [class*="evx-skeleton"], main [aria-busy="true"]:not(button, a, input, select, textarea, [role="button"])';

/**
 * Loads a phone list is holding inside `useDelayedSkeleton`'s quiet first 300 ms, when it draws
 * nothing at all and so nothing in the document says it is loading.
 */
let heldLoads = 0;

/**
 * On the root while a held load is under way, so a reader outside this module (an e2e spec timing
 * the launch settle) sees the same "still loading" this module does, held loads and all.
 */
export const CONTENT_HELD_ATTRIBUTE = 'data-content-held';

function markHeldLoads(): void {
  if (typeof document === 'undefined') return;
  if (heldLoads > 0) document.documentElement.setAttribute(CONTENT_HELD_ATTRIBUTE, '');
  else document.documentElement.removeAttribute(CONTENT_HELD_ATTRIBUTE);
}

/** Marks a load as under way until the returned release is called. */
export function holdContentPending(): () => void {
  heldLoads += 1;
  markHeldLoads();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    heldLoads -= 1;
    markHeldLoads();
  };
}

/** Whether the screen is still loading: a placeholder is drawn, or a held load is under way. */
export function isContentPending(): boolean {
  return heldLoads > 0 || document.querySelector(CONTENT_PENDING) !== null;
}

/** Frames with nothing pending before the content counts as in: one screen's stages can leave a gap. */
export const LAUNCH_QUIET_FRAMES = 3;

/**
 * The same quiet run in time, from the last frame anything was loading: a fast display draws three
 * frames in 25 ms, shorter than the gap between a screen's stages. Measured on a launch at the home
 * (I-2059, 40 launches at load 40, 120 Hz): the work-item list's two stages left a gap in 15, of 20
 * to 34 ms from its first quiet frame (about 42 ms from the last loading one), and three frames
 * ended the wait inside it, so the list's skeleton drew under the fade. 100 ms is over twice the
 * longest gap, and costs a launch at most that much more over a page already in.
 */
export const LAUNCH_QUIET_MS = 100;

/**
 * I-1234 (LS-2): run `callback` once the first screen's content is in — nothing pending
 * (`isContentPending`) for `LAUNCH_QUIET_FRAMES` frames running and `LAUNCH_QUIET_MS` since the last
 * frame something was. The iPhone shell keeps its launch mark up until then, so a launch goes from
 * the mark straight to the page and never shows a skeleton after it (one loading state, never two
 * in a row).
 */
export function whenLaunchContentSettles(callback: () => void): () => void {
  const started = performance.now();
  let frame = 0;
  let quiet = 0;
  let lastLoading = started;
  let cancelled = false;
  const check = () => {
    if (cancelled) return;
    const now = performance.now();
    if (isContentPending()) {
      quiet = 0;
      lastLoading = now;
    } else quiet += 1;
    const quietEnough = quiet >= LAUNCH_QUIET_FRAMES && now - lastLoading >= LAUNCH_QUIET_MS;
    if (!quietEnough && now - started < DATA_SETTLE_LIMIT_MS) {
      frame = requestAnimationFrame(check);
      return;
    }
    callback();
  };
  frame = requestAnimationFrame(check);
  return () => {
    cancelled = true;
    cancelAnimationFrame(frame);
  };
}

function whenNoneBusy(selector: string, callback: () => void): () => void {
  const started = performance.now();
  let frame = 0;
  let cancelled = false;
  const check = () => {
    if (cancelled) return;
    const waiting =
      document.querySelector(selector) !== null &&
      performance.now() - started < DATA_SETTLE_LIMIT_MS;
    if (waiting) {
      frame = requestAnimationFrame(check);
      return;
    }
    frame = requestAnimationFrame(() => {
      if (!cancelled) callback();
    });
  };
  frame = requestAnimationFrame(check);
  return () => {
    cancelled = true;
    cancelAnimationFrame(frame);
  };
}

/**
 * Move focus to the new screen's heading, so VoiceOver reads the screen once and from its top
 * (AC-13). The shell posts `.screenChanged` itself; the web's part is where focus lands.
 *
 * The first `h1` inside `<main>`, else `<main>` itself. Each is made focusable by script only
 * (`tabindex="-1"`), so Tab order does not change, and focused without scrolling.
 */
export function moveFocusToScreenHeading(): void {
  // I-1166: never behind an open dialog or sheet (every one is `ModalFrame`'s `aria-modal`): focus
  // taken from it broke its Escape and left its trap holding nothing.
  if (document.querySelector('[aria-modal="true"]')) return;
  const main = document.querySelector('main');
  const target = main?.querySelector<HTMLElement>('h1') ?? main ?? document.querySelector('h1');
  if (!target) return;
  // A field the person is typing in keeps focus: a title that updates late must not steal it.
  const active = document.activeElement;
  if (active && active !== document.body && target.contains(active) === false) {
    if (active.matches('input, textarea, select, [contenteditable="true"]')) return;
  }
  if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
  target.focus({ preventScroll: true });
}
